/**
 * A customized resize handle component for use with react-resizable-panels.
 * This component provides a draggable handle with a tooltip for resizing panels
 * within a PanelGroup layout.
 *
 * @example
 * ```tsx
 * <PanelGroup direction="horizontal">
 *   <Panel>Left panel content</Panel>
 *   <ResizeHandleWithTooltip />
 *   <Panel>Right panel content</Panel>
 * </PanelGroup>
 * ```
 *
 * The handle supports both horizontal and vertical resizing based on the direction prop,
 * and includes visual feedback during hover and drag interactions.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Box, styled } from '@mui/material';
import type { CSSObject, Theme } from '@mui/material/styles';

import {
  PanelResizeHandle,
  PanelResizeHandleProps,
} from 'react-resizable-panels';

import Tooltip from '@watershed/ui-core/components/Tooltip';

const TOOLTIP_DELAYS = {
  enter: 800,
  leave: 0,
} as const;

const RESIZE_HANDLE = {
  handleWidth: 8,
  indicatorWidth: 1,
  indicatorHoverWidth: 2,
  indicatorDragWidth: 4,
} as const;

type ResizeHandleDirection = 'vertical' | 'horizontal';

type ResizeHandleStyleProps = {
  theme: Theme;
  $direction?: ResizeHandleDirection;
  $hideIndicatorLine?: boolean;
};

function getIndicatorSizeStyles(
  direction: ResizeHandleDirection,
  width: number
) {
  return direction === 'vertical'
    ? { height: `${width}px` }
    : { width: `${width}px` };
}

function getIndicatorOpacityStyles(hideIndicatorLine: boolean) {
  return {
    opacity: hideIndicatorLine ? 0 : 1,
  };
}

export function getResizeHandleStyles({
  theme,
  $direction = 'horizontal',
  $hideIndicatorLine = false,
}: ResizeHandleStyleProps) {
  const direction = $direction;

  return {
    position: 'relative',
    // For vertical alignment, handle is horizontal (full width, fixed height)
    // For horizontal alignment, handle is vertical (fixed width, full height)
    width: direction === 'vertical' ? '100%' : `${RESIZE_HANDLE.handleWidth}px`,
    height:
      direction === 'vertical' ? `${RESIZE_HANDLE.handleWidth}px` : '100%',
    backgroundColor: 'transparent',
    zIndex: theme.zIndex.tooltip,
    margin:
      direction === 'vertical'
        ? `-${RESIZE_HANDLE.handleWidth / 2}px 0`
        : `0 -${RESIZE_HANDLE.handleWidth / 2}px`,
    '&:focus, &:focus-visible': {
      outline: 'none',
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      ...(direction === 'vertical'
        ? {
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
          }
        : {
            top: 0,
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          }),
      ...getIndicatorSizeStyles(direction, RESIZE_HANDLE.indicatorWidth),
      backgroundColor: theme.palette.grey20,
      borderRadius: theme.shape.borderRadius,
      ...getIndicatorOpacityStyles($hideIndicatorLine),
      transition: theme.transitions.create(['opacity', 'width', 'height'], {
        duration: theme.transitions.duration.standard,
      }),
    },

    '&[data-resize-handle-active="pointer"]::before': {
      ...getIndicatorOpacityStyles($hideIndicatorLine),
      backgroundColor: theme.palette.grey20,
      ...getIndicatorSizeStyles(direction, RESIZE_HANDLE.indicatorHoverWidth),
    },

    '&:hover': {
      '&::before': {
        ...getIndicatorOpacityStyles($hideIndicatorLine),
        backgroundColor: theme.palette.grey20,
        ...getIndicatorSizeStyles(direction, RESIZE_HANDLE.indicatorHoverWidth),
      },
    },

    '&[data-resize-handle-state="drag"]::before': {
      ...getIndicatorOpacityStyles($hideIndicatorLine),
      backgroundColor: theme.palette.grey30,
      ...getIndicatorSizeStyles(direction, RESIZE_HANDLE.indicatorDragWidth),
    },
  };
}

const StyledPanelResizeHandle = styled(PanelResizeHandle, {
  shouldForwardProp: (prop) =>
    prop !== '$direction' && prop !== '$hideIndicatorLine',
})<{
  $direction?: 'vertical' | 'horizontal';
  $hideIndicatorLine?: boolean;
}>(
  ({ theme, $direction, $hideIndicatorLine }) =>
    getResizeHandleStyles({
      theme,
      $direction,
      $hideIndicatorLine,
    }) as CSSObject
);

export const ResizeHandleWithTooltip = memo(
  ({
    direction = 'horizontal',
    hideIndicatorLine = false,
    ...props
  }: PanelResizeHandleProps & {
    direction?: 'vertical' | 'horizontal';
    hideIndicatorLine?: boolean;
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Only handle hover events on the blue indicator
    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, TOOLTIP_DELAYS.enter);
    };

    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, TOOLTIP_DELAYS.leave);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <Tooltip
        title={t`Drag to resize`}
        placement={direction === 'vertical' ? 'top' : 'left'}
        open={showTooltip && !isDragging}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -2],
                },
              },
            ],
            style: {
              pointerEvents: 'none',
            },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'flex',
            cursor: direction === 'vertical' ? 'row-resize' : 'col-resize',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <StyledPanelResizeHandle
            {...props}
            $direction={direction}
            $hideIndicatorLine={hideIndicatorLine}
            onDragging={(dragging) => {
              setIsDragging(dragging);
              props.onDragging?.(dragging);
            }}
          />
        </Box>
      </Tooltip>
    );
  }
);
